import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import { loadPlaysReview, type PlayReview } from '@/lib/team-scope';
import { WORK_GUIDANCE_MOTIF_LABELS, type WorkGuidanceMotif } from '@/lib/work-guidance';

/** The first day of this month, moved by `offset` months. */
const monthStart = (offset: number) => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + offset, 1);
};
const monthLabel = (start: Date) => start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

/**
 * La revue des coups (company scope, 21/10/2026): each app with a list, over a
 * month — objects found, where they stand, actions launched from them, and the
 * motives given when setting them aside, counted without names. « Ajuster »
 * opens the app in the Forge; the decision is kept in the ritual that opened it.
 */
export const PlaysReview = () => {
  const [offset, setOffset] = useState(0);
  const [plays, setPlays] = useState<PlayReview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const from = monthStart(offset);

  useEffect(() => {
    setPlays(null);
    void loadPlaysReview(monthStart(offset), monthStart(offset + 1)).then(setPlays)
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'La revue des coups n’est pas accessible.'));
  }, [offset]);

  if (error) return <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-domino-ink">
        <button type="button" aria-label="Mois précédent" onClick={() => setOffset(offset - 1)} className="rounded p-1 hover:bg-domino-muted"><ChevronLeft className="h-4 w-4" /></button>
        <span className="min-w-32 text-center first-letter:uppercase">{monthLabel(from)}</span>
        <button type="button" aria-label="Mois suivant" disabled={offset >= 0} onClick={() => setOffset(offset + 1)} className="rounded p-1 hover:bg-domino-muted disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {!plays ? (
        <SkeletonGroup label="Chargement de la revue" className="space-y-2">
          {[0, 1].map((index) => <Skeleton key={index} className="h-14 w-full" />)}
        </SkeletonGroup>
      ) : plays.length === 0 ? (
        <p className="py-6 text-sm text-domino-inkSoft">Aucune application avec une liste.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-domino-cardBorder bg-domino-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-domino-inkSoft">
                <th className="px-3 py-2 font-medium">Application</th>
                <th className="px-3 py-2 font-medium">Fiches du mois</th>
                <th className="px-3 py-2 font-medium">Où elles en sont</th>
                <th className="px-3 py-2 font-medium">Actions lancées</th>
                <th className="px-3 py-2 font-medium">Motifs d’écart</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {plays.map((play) => (
                <tr key={play.toolAppId} className="border-t border-domino-cardBorder align-top">
                  <td className="px-3 py-2.5">
                    <Link to={`/apps/${encodeURIComponent(play.appKey)}`} className="font-semibold text-domino-aubergine hover:underline">{play.title}</Link>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">{play.created || ''}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {play.statuses.filter((status) => status.count > 0).map((status) => (
                        <span key={status.key} className="rounded-full bg-domino-muted px-2 py-0.5 text-xs text-domino-ink">{status.label} <span className="tabular-nums">{status.count}</span></span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">{play.launched || ''}</td>
                  <td className="px-3 py-2.5 text-xs text-domino-ink">
                    {Object.entries(play.setAside).map(([motif, total]) => (
                      <span key={motif} className="block">{WORK_GUIDANCE_MOTIF_LABELS[motif as WorkGuidanceMotif] ?? motif} <span className="tabular-nums">× {total}</span></span>
                    ))}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {play.flowId && (
                      <Link to={`/forge?flowId=${encodeURIComponent(play.flowId)}`}
                        className={`inline-flex min-h-8 items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 ${DENSE_ACTION_CLASS}`}>
                        Ajuster
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
